import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Reveal, Section, scrollToId } from "./shared";
import { LeadForm } from "./LeadForm";

export const FinalCTA = ({ businessName, onBusinessName }) => (
    <Section id="become" className="bg-ink text-white">
        <div className="grid items-center gap-14 lg:grid-cols-2">
            <div>
                <Reveal>
                    <h2 className="font-display text-4xl font-bold leading-[1.06] tracking-[-0.02em] sm:text-5xl">
                        You’ve already built something worth choosing.{" "}
                        <span className="text-white/60">Let’s give more people a reason to choose it.</span>
                    </h2>
                </Reveal>
                <Reveal delay={0.16}>
                    <p className="mt-6 max-w-lg text-base text-white/70 sm:text-lg">
                        Start with your business name. Preview your Purple page and join the waitlist for what comes
                        next. More ways to be discovered. More ideas worth trying. More reasons for customers to return.
                    </p>
                </Reveal>
            </div>
            <Reveal delay={0.15}>
                <LeadForm businessName={businessName} onBusinessName={onBusinessName} dark />
            </Reveal>
        </div>
    </Section>
);

const FAQS = [
    ["I want more people to discover me.", "Someone near you may already be looking for what you offer. Purple helps you show up with a clear page, an active Google presence and ads that reach beyond your existing circle."],
    ["I’ve never run Google or Meta ads.", "Purple helps you create an ad, choose a budget and understand the response — you don’t have to learn every setting before you begin."],
    ["I need better offers and campaign ideas.", "Bring the rough idea. Purple helps you explore offers, audiences and angles until one fits your business — then turns it into a plan you can approve."],
    ["I want to create better content.", "Search ads, banners, posts and video scripts shaped around your business. You edit, approve and launch — no production brief required."],
    ["I want customers to come back more often.", "Learn what brings people back: feedback themes, repeat-visit offers and small experience improvements you can act on."],
    ["I’ve never run an ad. Can I use Purple?", "That’s one of the starting points Purple is designed for. Begin with your business and goal; Purple helps you work through the audience, offer, creative and campaign."],
    ["Will Purple run everything without asking me?", "Choose automation settings for supported routine tasks. Review campaigns, budgets and significant changes through the approval flow."],
    ["I already have a marketer or agency. Is Purple still useful?", "Purple can help keep business context, ideas, creative work and performance together. You can use it alongside the people already helping you."],
    ["Is advertising spend included in the monthly price?", "Full pricing details are coming soon, including how platform ad spend, credits and expert support are charged."],
    ["Does every plan include expert calls?", "Call availability and pricing will be listed with the full plan details."],
    ["Will a Purple page guarantee a better Google ranking?", "No. It gives customers another useful place to learn about your business. Google visibility depends on multiple factors; Purple helps you improve supported parts of your profile and online presence."],
    ["Will Purple guarantee 3× revenue?", "No. The calculator lets you explore goals using assumptions you can see and change. Actual outcomes depend on your business, offer, budget, execution and customers."],
    ["Where can I get the app?", "Join the waitlist for availability updates. App Store and Google Play links will be added once the apps are live."],
];

export const Faq = () => (
    <Section id="faq">
        <Reveal className="mx-auto max-w-3xl text-center">
            <h2 className="font-display text-4xl font-bold leading-[1.06] tracking-[-0.02em] text-ink sm:text-5xl">
                Before you decide.
            </h2>
        </Reveal>
        <Reveal delay={0.1} className="mx-auto mt-12 max-w-3xl">
            <Accordion type="single" collapsible>
                {FAQS.map(([q, a], i) => (
                    <AccordionItem key={i} value={`faq-${i}`} className="border-b border-neutral-200">
                        <AccordionTrigger data-testid={`faq-question-${i}`} className="py-5 text-left font-display text-lg font-bold text-ink hover:text-neutral-500 hover:no-underline">
                            {q}
                        </AccordionTrigger>
                        <AccordionContent className="pb-5 text-base leading-relaxed text-neutral-600">
                            {a}
                        </AccordionContent>
                    </AccordionItem>
                ))}
            </Accordion>
        </Reveal>
    </Section>
);

const FOOTER_COLS = [
    {
        title: "Explore",
        links: [
            ["How it works", "#how-it-works", "footer-link-how-it-works"],
            ["What you can do", "#what-you-can-do", "footer-link-what-you-can-do"],
            ["Expert help", "#team", "footer-link-expert-help"],
            ["Pricing", "#pricing", "footer-link-pricing"],
        ],
    },
    {
        title: "The product",
        links: [
            ["My Business", "#what-you-can-do", "footer-link-my-business"],
            ["My Customer", "#what-you-can-do", "footer-link-my-customer"],
            ["Planner", "#what-you-can-do", "footer-link-planner"],
            ["Ads", "#what-you-can-do", "footer-link-ads"],
            ["Tools", "#what-you-can-do", "footer-link-tools"],
        ],
    },
    {
        title: "Company",
        links: [
            ["Founders", "#founders", "footer-link-founders"],
            ["FAQ", "#faq", "footer-link-faq"],
            ["Join the waitlist", "#become", "footer-link-waitlist"],
        ],
    },
];

export const Footer = () => (
    <footer className="mt-1.5 rounded-t-[2.5rem] bg-white text-ink sm:mt-2.5" data-testid="footer">
        <div className="mx-auto max-w-6xl px-5 py-16 sm:px-8">
            <div className="grid gap-12 md:grid-cols-5">
                <div className="md:col-span-2">
                    <img src="/assets/logo-dark.png" alt="Purple Business" className="h-10 w-auto rounded-[10px]" />
                    <p className="mt-5 max-w-xs text-sm leading-relaxed text-neutral-500">
                        AI for small business growth. Reach new customers, make the everyday work easier, and keep
                        improving the business you’ve built.
                    </p>
                </div>
                {FOOTER_COLS.map((col) => (
                    <div key={col.title}>
                        <p className="text-xs font-bold uppercase tracking-[0.18em] text-neutral-400">{col.title}</p>
                        <ul className="mt-4 space-y-3">
                            {col.links.map(([label, href, id]) => (
                                <li key={id}>
                                    <button
                                        data-testid={id}
                                        onClick={() => scrollToId(href)}
                                        className="text-sm font-bold text-neutral-600 transition-colors hover:text-ink"
                                    >
                                        {label}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>
                ))}
            </div>
            <div className="mt-14 flex flex-col items-center justify-between gap-4 border-t border-neutral-200 pt-8 text-xs text-neutral-400 sm:flex-row">
                <p>© 2026 Purple Business. All rights reserved.</p>
                <p>purplebusiness.in</p>
            </div>
        </div>
    </footer>
);
